import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus,
  FolderOpen,
  Clock,
  Database,
  Loader2,
  MoreVertical,
  Trash2,
  Edit
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import Sidebar from '../components/Sidebar';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function DashboardPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error(language === 'zh' ? '获取项目失败' : 'Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const createProject = async () => {
    if (!newProjectName.trim()) {
      toast.error(language === 'zh' ? '请输入项目名称' : 'Please enter project name');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API_URL}/projects`, {
        name: newProjectName,
        description: newProjectDesc,
        system_prompt: ''
      });
      setProjects([...projects, response.data]);
      setShowNewProject(false);
      setNewProjectName('');
      setNewProjectDesc('');
      toast.success(language === 'zh' ? '项目创建成功' : 'Project created successfully');
      navigate(`/editor/${response.data.id}`);
    } catch (error) {
      toast.error(language === 'zh' ? '创建项目失败' : 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const deleteProject = async (projectId, e) => {
    e.stopPropagation();
    if (!window.confirm(language === 'zh' ? '确定要删除这个项目吗？' : 'Are you sure you want to delete this project?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/projects/${projectId}`);
      setProjects(projects.filter(p => p.id !== projectId));
      toast.success(language === 'zh' ? '项目已删除' : 'Project deleted');
    } catch (error) {
      toast.error(language === 'zh' ? '删除失败' : 'Failed to delete');
    }
  };

  const getStatusColor = (count) => {
    if (count < 10) return 'text-red-600 bg-red-50 border-red-200';
    if (count < 20) return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-emerald-600 bg-emerald-50 border-emerald-200';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="main-layout" data-testid="dashboard-page">
      <Sidebar />

      <main className="content-container">
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {t('dashboard.title')}
              </h1>
              <p className="text-slate-500 mt-1">
                {language === 'zh' ? '管理你的微调数据项目' : 'Manage your fine-tuning data projects'}
              </p>
            </div>
            <Button
              onClick={() => setShowNewProject(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="new-project-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('dashboard.newProject')}
            </Button>
          </div>

          {/* Projects Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : projects?.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <FolderOpen className="w-10 h-10 text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-700 mb-2">
                {t('dashboard.noProjects')}
              </h3>
              <p className="text-slate-500 mb-6 max-w-sm">
                {t('dashboard.noProjectsDesc')}
              </p>
              <Button
                onClick={() => setShowNewProject(true)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="empty-new-project-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('dashboard.newProject')}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects?.map((project, index) => (
                <div
                  key={project.id}
                  className={`project-card animate-fade-in-up stagger-${Math.min(index + 1, 5)}`}
                  onClick={() => navigate(`/editor/${project.id}`)}
                  data-testid={`project-card-${project.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold text-slate-900 truncate flex-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {project.name}
                    </h3>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <button className="p-1 hover:bg-slate-100 rounded" data-testid={`project-menu-${project.id}`}>
                          <MoreVertical className="w-4 h-4 text-slate-400" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/editor/${project.id}`); }}>
                          <Edit className="w-4 h-4 mr-2" />
                          {t('common.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => deleteProject(project.id, e)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {t('common.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {project.description && (
                    <p className="text-slate-500 text-sm mb-4 line-clamp-2">
                      {project.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-slate-400" />
                      <span className={`text-sm font-medium px-2 py-0.5 rounded-full border ${getStatusColor(project.sample_count)}`}>
                        {project.sample_count} {t('dashboard.samples')}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-slate-400 text-xs">
                      <Clock className="w-3 h-3" />
                      {formatDate(project.updated_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* New Project Dialog */}
      <Dialog open={showNewProject} onOpenChange={setShowNewProject}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {t('dashboard.newProject')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('editor.projectName')}</Label>
              <Input
                id="name"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder={language === 'zh' ? '例如：客服助手' : 'e.g., Customer Service Bot'}
                className="input-focus-ring"
                data-testid="new-project-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">{t('editor.description')}</Label>
              <Input
                id="description"
                value={newProjectDesc}
                onChange={(e) => setNewProjectDesc(e.target.value)}
                placeholder={language === 'zh' ? '简短描述...' : 'Brief description...'}
                className="input-focus-ring"
                data-testid="new-project-desc-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewProject(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={createProject}
              disabled={creating}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="create-project-btn"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                t('dashboard.newProject')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
